import { useMemo, useState } from "react";

import { AdminButton } from "@/components/admin/button";
import { AdminIcon } from "@/components/admin/icon";
import { useToast } from "@/components/ui/feedback";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import {
	useCreateOffer,
	useCreateVariant,
	useHubs,
	useSetStock,
	useUpdateProduct,
} from "@/hooks/use-catalog";
import {
	type Product,
	type ProductOption,
	type ProductSpec,
	readProductOptions,
	readProductSpecs,
} from "@/lib/catalog/types";

const MAX_COMBOS = 60;
const DEFAULT_COLOR = "#C6563A";

/** Signature stable d'une combinaison (pour dédupliquer avec l'existant). */
const comboKey = (values: Record<string, string>) =>
	JSON.stringify(Object.entries(values).sort(([a], [b]) => a.localeCompare(b)));

/** Pastille de couleur (lecture seule). */
export function ColorSwatch({
	color,
	size = 14,
}: {
	color: string;
	size?: number;
}) {
	return (
		<span
			style={{
				display: "inline-block",
				width: size,
				height: size,
				borderRadius: "50%",
				background: color,
				border: "1px solid rgba(0,0,0,0.18)",
				verticalAlign: "middle",
				flexShrink: 0,
			}}
		/>
	);
}

// ─── Carte « Options (axes de déclinaison) » ────────────────────────────────

export function ProductOptionsCard({ product }: { product: Product }) {
	const toast = useToast();
	const updateMut = useUpdateProduct(product.id);
	const createVariantMut = useCreateVariant(product.id);
	const createOfferMut = useCreateOffer(product.id);
	const setStockMut = useSetStock(product.id);
	const { data: hubs = [] } = useHubs();
	const [options, setOptions] = useState<ProductOption[]>(() =>
		readProductOptions(product.attributes),
	);
	const [dirty, setDirty] = useState(false);
	const [generating, setGenerating] = useState(false);
	const [picker, setPicker] = useState<{ i: number; vi: number } | null>(null);
	// Images produit (niveau produit, avec URL) disponibles pour associer aux couleurs.
	const productImages = useMemo(
		() => product.mediaLinks.filter((m) => m.url && m.variantId == null),
		[product.mediaLinks],
	);
	const mediaUrl = (mediaId?: string) =>
		mediaId
			? (productImages.find((m) => m.mediaId === mediaId)?.url ?? null)
			: null;

	const patch = (next: ProductOption[]) => {
		setOptions(next);
		setDirty(true);
	};

	const addOption = () =>
		patch([...options, { name: "", type: "text", values: [] }]);
	const removeOption = (i: number) => patch(options.filter((_, x) => x !== i));
	const setOption = (i: number, o: Partial<ProductOption>) =>
		patch(options.map((opt, x) => (x === i ? { ...opt, ...o } : opt)));

	const addValue = (i: number) => {
		const opt = options[i];
		setOption(i, {
			values: [
				...opt.values,
				{
					label: "",
					...(opt.type === "color" ? { color: DEFAULT_COLOR } : {}),
				},
			],
		});
	};
	const setValue = (
		i: number,
		vi: number,
		v: Partial<{ label: string; color: string; mediaId: string }>,
	) => {
		const opt = options[i];
		setOption(i, {
			values: opt.values.map((val, x) => (x === vi ? { ...val, ...v } : val)),
		});
	};
	const removeValue = (i: number, vi: number) => {
		const opt = options[i];
		setOption(i, { values: opt.values.filter((_, x) => x !== vi) });
	};

	/** Nettoie (noms/valeurs vides) puis persiste dans attributes.options. */
	const cleanOptions = () =>
		options
			.map((o) => ({
				...o,
				name: o.name.trim(),
				values: o.values.filter((v) => v.label.trim() !== ""),
			}))
			.filter((o) => o.name !== "");

	const save = async (silent = false): Promise<ProductOption[] | null> => {
		const cleaned = cleanOptions();
		try {
			await updateMut.mutateAsync({
				attributes: { ...product.attributes, options: cleaned },
			});
			setOptions(cleaned);
			setDirty(false);
			if (!silent) toast.success("Options enregistrées");
			return cleaned;
		} catch (e) {
			toast.error(
				extractErrorMessage(e, "Échec de l'enregistrement des options"),
			);
			return null;
		}
	};

	/** Produit cartésien des valeurs → crée les variantes manquantes. */
	const generate = async () => {
		const saved = await save(true);
		if (!saved) return;
		const usable = saved.filter((o) => o.values.length > 0);
		if (usable.length === 0) {
			toast.error(
				"Définis au moins une option avec des valeurs avant de générer.",
			);
			return;
		}
		let combos: Array<Record<string, string>> = [{}];
		for (const opt of usable) {
			const next: Array<Record<string, string>> = [];
			for (const base of combos)
				for (const val of opt.values)
					next.push({ ...base, [opt.name]: val.label });
			combos = next;
			if (combos.length > MAX_COMBOS) {
				toast.error(
					`Trop de combinaisons (max ${MAX_COMBOS}). Réduis le nombre de valeurs.`,
				);
				return;
			}
		}
		const existing = new Set(
			product.variants.map((v) => comboKey(v.optionValues ?? {})),
		);
		const missing = combos.filter((c) => !existing.has(comboKey(c)));
		if (missing.length === 0) {
			toast.success("Toutes les combinaisons existent déjà.");
			return;
		}
		setGenerating(true);
		let created = 0;
		// Hub par défaut pour l'offre et la ligne de stock initiale (YAO sinon le 1er actif).
		const defaultHub = hubs.find((h) => h.code === "YAO") ?? hubs[0];
		try {
			for (const optionValues of missing) {
				const name = usable.map((o) => optionValues[o.name]).join(" / ");
				const variant = await createVariantMut.mutateAsync({
					name,
					optionValues,
				});
				// Offre au prix de base du produit (publiable immédiatement)…
				await createOfferMut.mutateAsync({
					variantId: variant.id,
					payload: {
						price: product.basePrice,
						compareAtPrice: product.compareAtPrice ?? null,
						currency: product.currency,
						preferredHubId: defaultHub?.id ?? null,
					},
				});
				// …et stock initial à 10 (à ajuster dans l'étape Tarification & stock).
				if (defaultHub) {
					await setStockMut.mutateAsync({
						variantId: variant.id,
						hubId: defaultHub.id,
						quantity: 10,
					});
				}
				created++;
			}
			toast.success(
				`${created} variante${created > 1 ? "s" : ""} créée${created > 1 ? "s" : ""} avec offre au prix de base et stock à 10 (${combos.length - created} existante${combos.length - created > 1 ? "s" : ""}).`,
			);
		} catch (e) {
			toast.error(
				extractErrorMessage(
					e,
					`Génération interrompue après ${created} variante(s)`,
				),
			);
		} finally {
			setGenerating(false);
		}
	};

	return (
		<div className="card">
			<div className="card-h">
				<div>
					<h3>Options du produit</h3>
					<p className="sub muted">
						Axes de déclinaison (Couleur, Taille…) — combine-les pour générer
						les variantes
					</p>
				</div>
				<div style={{ display: "flex", gap: 8 }}>
					<AdminButton variant="ghost" size="sm" onClick={addOption}>
						<AdminIcon n="plus" s={12} />
						Option
					</AdminButton>
					<AdminButton
						variant="ghost"
						size="sm"
						onClick={() => void save()}
						disabled={!dirty || updateMut.isPending}
					>
						{updateMut.isPending ? "…" : dirty ? "Enregistrer" : "Enregistré ✓"}
					</AdminButton>
				</div>
			</div>
			<div className="card-b">
				{options.length === 0 && (
					<p className="muted" style={{ margin: 0, fontSize: 12 }}>
						Aucune option. Exemple : ajoute « Couleur » (type Couleur) avec
						Rouge/Bleu, puis « Taille » avec S/M/L, et génère les 6 variantes en
						un clic.
					</p>
				)}
				{options.map((opt, i) => (
					<div
						key={i}
						style={{
							border: "1px solid var(--a-border)",
							borderRadius: 6,
							padding: "10px 12px",
							marginBottom: 10,
						}}
					>
						<div
							style={{
								display: "flex",
								gap: 8,
								alignItems: "center",
								marginBottom: 8,
							}}
						>
							<input
								className="aform-i"
								placeholder="Nom de l'option (ex. Couleur)"
								value={opt.name}
								onChange={(e) => setOption(i, { name: e.target.value })}
								style={{ maxWidth: 220, fontWeight: 600 }}
								data-testid={`opt-name-${i}`}
							/>
							<select
								className="aform-i"
								value={opt.type}
								onChange={(e) => {
									const type = e.target.value as ProductOption["type"];
									setOption(i, {
										type,
										values: opt.values.map((v) =>
											type === "color"
												? { ...v, color: v.color ?? DEFAULT_COLOR }
												: { label: v.label },
										),
									});
								}}
								style={{ maxWidth: 130 }}
								data-testid={`opt-type-${i}`}
							>
								<option value="text">Texte</option>
								<option value="color">Couleur</option>
							</select>
							<div style={{ flex: 1 }} />
							<button
								type="button"
								className="top-btn"
								onClick={() => removeOption(i)}
								title="Supprimer cette option"
								style={{ color: "var(--a-danger)" }}
							>
								<AdminIcon n="close" s={14} />
							</button>
						</div>
						<div
							style={{
								display: "flex",
								flexWrap: "wrap",
								gap: 8,
								alignItems: "center",
							}}
						>
							{opt.values.map((val, vi) => (
								<span
									key={vi}
									style={{
										display: "inline-flex",
										alignItems: "center",
										gap: 6,
										border: "1px solid var(--a-border)",
										borderRadius: 6,
										padding: "4px 6px",
										background: "var(--a-surface-2)",
									}}
								>
									{opt.type === "color" && (
										<input
											type="color"
											value={val.color ?? DEFAULT_COLOR}
											onChange={(e) =>
												setValue(i, vi, { color: e.target.value })
											}
											title="Choisir la couleur (pipette)"
											style={{
												width: 26,
												height: 26,
												padding: 0,
												border: "none",
												background: "transparent",
												cursor: "pointer",
											}}
											data-testid={`opt-${i}-color-${vi}`}
										/>
									)}
									<input
										className="aform-i"
										placeholder={
											opt.type === "color"
												? "Nom (ex. Rouge)"
												: "Valeur (ex. M)"
										}
										value={val.label}
										onChange={(e) => setValue(i, vi, { label: e.target.value })}
										style={{ width: 120, padding: "4px 8px", fontSize: 12 }}
										data-testid={`opt-${i}-label-${vi}`}
									/>
									{opt.type === "color" && val.color && (
										<span className="mono muted" style={{ fontSize: 10 }}>
											{val.color.toUpperCase()}
										</span>
									)}
									{opt.type === "color" && (
										<button
											type="button"
											onClick={() =>
												setPicker(
													picker?.i === i && picker?.vi === vi
														? null
														: { i, vi },
												)
											}
											title="Associer une image à cette couleur (la photo change au clic sur le site)"
											style={{
												width: 24,
												height: 24,
												padding: 0,
												borderRadius: 4,
												cursor: "pointer",
												display: "grid",
												placeItems: "center",
												border: mediaUrl(val.mediaId)
													? "1px solid var(--a-border)"
													: "1px dashed var(--a-border)",
												background: mediaUrl(val.mediaId)
													? `center/cover no-repeat url(${mediaUrl(val.mediaId)})`
													: "var(--a-surface)",
											}}
											data-testid={`opt-${i}-image-${vi}`}
										>
											{!mediaUrl(val.mediaId) && <AdminIcon n="plus" s={11} />}
										</button>
									)}
									<button
										type="button"
										onClick={() => removeValue(i, vi)}
										title="Retirer"
										style={{
											border: "none",
											background: "none",
											cursor: "pointer",
											color: "var(--a-muted)",
											padding: 0,
											display: "grid",
										}}
									>
										<AdminIcon n="close" s={11} />
									</button>
								</span>
							))}
							<AdminButton
								variant="ghost"
								size="sm"
								onClick={() => addValue(i)}
							>
								<AdminIcon n="plus" s={11} />
								Valeur
							</AdminButton>
						</div>
						{picker?.i === i && opt.type === "color" && (
							<div
								style={{
									marginTop: 10,
									padding: 10,
									border: "1px dashed var(--a-border)",
									borderRadius: 6,
									background: "var(--a-surface-2)",
								}}
							>
								<div
									style={{
										fontSize: 11,
										marginBottom: 6,
										color: "var(--a-muted)",
									}}
								>
									Image pour «&nbsp;{opt.values[picker.vi]?.label || "—"}&nbsp;»
									— clic pour associer
								</div>
								{productImages.length === 0 ? (
									<p className="muted" style={{ margin: 0, fontSize: 11 }}>
										Ajoute d'abord des images dans l'onglet Médias.
									</p>
								) : (
									<div
										style={{
											display: "flex",
											flexWrap: "wrap",
											gap: 6,
											alignItems: "center",
										}}
									>
										{productImages.map((m) => (
											<button
												key={m.id}
												type="button"
												onClick={() => {
													setValue(i, picker.vi, { mediaId: m.mediaId });
													setPicker(null);
												}}
												title={m.altText ?? ""}
												style={{
													width: 48,
													height: 48,
													borderRadius: 6,
													cursor: "pointer",
													padding: 0,
													border:
														opt.values[picker.vi]?.mediaId === m.mediaId
															? "2px solid #C6563A"
															: "1px solid var(--a-border)",
													background: `center/cover no-repeat url(${m.url})`,
												}}
												data-testid={`picker-img-${m.mediaId}`}
											/>
										))}
										<AdminButton
											variant="ghost"
											size="sm"
											onClick={() => {
												setValue(i, picker.vi, { mediaId: undefined });
												setPicker(null);
											}}
										>
											Aucune
										</AdminButton>
									</div>
								)}
							</div>
						)}
					</div>
				))}
				{options.length > 0 && (
					<AdminButton
						variant="primary"
						onClick={() => void generate()}
						disabled={generating || createVariantMut.isPending}
					>
						<AdminIcon n={generating ? "refresh" : "star"} s={14} />
						{generating
							? "Génération…"
							: "Générer les variantes (combinaisons)"}
					</AdminButton>
				)}
			</div>
		</div>
	);
}

// ─── Carte « Caractéristiques » ─────────────────────────────────────────────

export function ProductSpecsCard({ product }: { product: Product }) {
	const toast = useToast();
	const updateMut = useUpdateProduct(product.id);
	const [specs, setSpecs] = useState<ProductSpec[]>(() =>
		readProductSpecs(product.attributes),
	);
	const [dirty, setDirty] = useState(false);

	const patch = (next: ProductSpec[]) => {
		setSpecs(next);
		setDirty(true);
	};

	const save = async () => {
		const cleaned = specs
			.map((s) => ({ label: s.label.trim(), value: s.value.trim() }))
			.filter((s) => s.label !== "" && s.value !== "");
		try {
			await updateMut.mutateAsync({
				attributes: { ...product.attributes, specs: cleaned },
			});
			setSpecs(cleaned);
			setDirty(false);
			toast.success("Caractéristiques enregistrées");
		} catch (e) {
			toast.error(extractErrorMessage(e, "Échec de l'enregistrement"));
		}
	};

	return (
		<div className="card">
			<div className="card-h">
				<div>
					<h3>Caractéristiques</h3>
					<p className="sub muted">
						Fiche technique affichée sur le site (onglet Caractéristiques)
					</p>
				</div>
				<div style={{ display: "flex", gap: 8 }}>
					<AdminButton
						variant="ghost"
						size="sm"
						onClick={() => patch([...specs, { label: "", value: "" }])}
					>
						<AdminIcon n="plus" s={12} />
						Ligne
					</AdminButton>
					<AdminButton
						variant="ghost"
						size="sm"
						onClick={() => void save()}
						disabled={!dirty || updateMut.isPending}
					>
						{updateMut.isPending ? "…" : dirty ? "Enregistrer" : "Enregistré ✓"}
					</AdminButton>
				</div>
			</div>
			<div className="card-b">
				{specs.length === 0 ? (
					<p className="muted" style={{ margin: 0, fontSize: 12 }}>
						Aucune caractéristique. Exemples : Matière → Coton bio · Poids → 800
						g · Origine → Bafoussam.
					</p>
				) : (
					specs.map((s, i) => (
						<div
							key={i}
							style={{
								display: "flex",
								gap: 8,
								marginBottom: 8,
								alignItems: "center",
							}}
						>
							<input
								className="aform-i"
								placeholder="Libellé (ex. Matière)"
								value={s.label}
								onChange={(e) =>
									patch(
										specs.map((x, xi) =>
											xi === i ? { ...x, label: e.target.value } : x,
										),
									)
								}
								style={{ maxWidth: 220 }}
								data-testid={`spec-label-${i}`}
							/>
							<input
								className="aform-i"
								placeholder="Valeur (ex. Coton bio)"
								value={s.value}
								onChange={(e) =>
									patch(
										specs.map((x, xi) =>
											xi === i ? { ...x, value: e.target.value } : x,
										),
									)
								}
								style={{ flex: 1 }}
								data-testid={`spec-value-${i}`}
							/>
							<button
								type="button"
								className="top-btn"
								onClick={() => patch(specs.filter((_, xi) => xi !== i))}
								title="Supprimer"
								style={{ color: "var(--a-danger)" }}
							>
								<AdminIcon n="close" s={14} />
							</button>
						</div>
					))
				)}
			</div>
		</div>
	);
}
